import React, { useContext } from 'react';
import Select from 'react-select';
import { RedSspan } from '../styles/RedSpan';
import { AuthContext } from '@/common/contexts/authContext';


const heightLimitStyles = {
	overflow: 'hidden',
};

const valueLimitStyles = {
	flexWrap: 'nowrap',
	flexShrink: 0,
	overflowX: 'scroll',
	overflowY: 'hidden',
};

const multiValueLimitStyles = {
	flexShrink: 0,
	minWidth: 'fit-content',
};

const multiValueLabelLimitStyles = {
	maxWidth: 'none',
	overflow: 'visible',
	textOverflow: 'unset',
	whiteSpace: 'nowrap',
};

const square = (color) => ({
	alignItems: 'center',
	display: 'flex',

	':before': {
		backgroundColor: color,
		borderRadius: 4,
		content: '" "',
		display: color ? 'block' : 'none',
		marginRight: 8,
		height: 18,
		width: 18,
	},
});

const customStyles = ({
	width,
	rotation,
	reversed,
	showSquare,
	placeholderColor,
	bgColor,
	fontSize,
	textColor,
	minHeight,
	maxHeight,
	dropdownIndicatorPadding,
}) => {
	const heightLimit = maxHeight
		? {
				maxHeight,
				...heightLimitStyles,
			}
		: {};
	const valueLimit = maxHeight
		? {
				maxHeight,
				...valueLimitStyles,
			}
		: {};
	const multiValueLimit = maxHeight
		? multiValueLimitStyles
		: {};
	const multiValueLabelLimit = maxHeight
		? multiValueLabelLimitStyles
		: {};

	return {
		container: (provided) => ({
			...provided,
			minHeight: minHeight,
			width: `${width}px`,
			fontSize: `${fontSize}px`,
		}),
		dropdownIndicator: (provided) => ({
			...provided,
			padding: dropdownIndicatorPadding,
		}),
		control: (provided, state) => ({
			...provided,
			minHeight: minHeight,
			borderRadius: '10px',
			width: `${width}px`,
			transform: rotation,
			backgroundColor: bgColor || provided.backgroundColor,
			color: textColor || provided.color,
			...heightLimit,
		}),
		valueContainer: (provided) => ({
			...provided,
			transform: rotation,
			...valueLimit,
		}),
		multiValue: (provided) => ({
			...provided,
			...multiValueLimit,
		}),
		multiValueLabel: (provided) => ({
			...provided,
			...multiValueLabelLimit,
		}),
		menuPortal: (provided) => ({
			...provided,
			zIndex: 9999,
		}),
		menu: (provided) => ({
			...provided,
			width: `${width}px`,
			maxHeight: '140px',
			transform: reversed && 'translateY(-140%)',
		}),
		menuList: (provided) => ({
			...provided,
			width: `${width}px`,
			maxHeight: '140px',
			backgroundColor: 'white',
		}),
		option: (provided, { data, isSelected }) => ({
			...provided,
			...square(showSquare && data.color),
			color: isSelected ? '#fff' : '#000',
		}),
		placeholder: (styles) => ({
			...styles,
			color: placeholderColor,
		}),
	};
};

const SelectInput = ({
	name,
	options,
	setSelected,
	selected,
	multiple = false,
	width = 300,
	selectWidth = 260,
	setAccesses,
	labelUp = false,
	showLabel = true,
	onChange = () => { },
	color = 'black',
	required = false,
	placeholder = 'Select...',
	id,
	disabled = false,
	fontSize = 18,
	margin = '10px',
	hideStar = false,
	customThemeColors,
	bgColor,
	rotation,
	reversed,
	showSquare,
	placeholderColor = '#000',
	selectFontSize = 16,
	strong = true,
	textColor = '#000',
	minHeight = '38px',
	maxHeight,
	dropdownIndicatorPadding = '8px',
	upGap = '10px',
	gap = '30px',
	textAlign = 'right',
	missingOptionsText = 'No options',
	isClearable,
	autoFocus = false,
	defaultMenuIsOpen = false,
	onBlur,
	menuPortalTarget,
}) => {
	const { getRolesAccesses } = useContext(AuthContext);

	const handleChange = (e) => {
		if (!e) {
			setSelected(null);
			return;
		}

		onChange(e.value);
		if (setAccesses) {
			const accesses = getRolesAccesses(e.label);
			setAccesses(() =>
				accesses.map((access) => {
					return { label: access, value: access };
				}),
			);
		}
		setSelected(e);
	};

	return (
		<div
			style={{
				display: labelUp ? 'grid' : 'flex',
				alignItems: 'center',
				gap: labelUp ? upGap : gap,
				margin: margin,
				borderRadius: '20px !important',
			}}
		>
			{showLabel && (
				<label
					style={{
						width: `${width}px`,
						textAlign,
						fontSize: `${fontSize}px`,
					}}
				>
					{strong ? (
						<strong>
							<span style={{ color }}>
								{name}: {required && !hideStar && <RedSspan>*</RedSspan>}
							</span>
						</strong>
					) : (
						<span style={{ color }}>
							{name}: {required && !hideStar && <RedSspan>*</RedSspan>}
						</span>
					)}
				</label>
			)}
			<div>
				<Select
					noOptionsMessage={() => missingOptionsText}
					required={required}
					id={id}
					theme={(theme) => {
						return customThemeColors
							? { ...theme, colors: customThemeColors }
							: theme;
					}}
					value={selected}
					onChange={(e) => handleChange(e)}
					options={options}
					isMulti={multiple}
					reversed
					styles={customStyles({
						width: selectWidth,
						rotation,
						reversed,
						showSquare,
						placeholderColor,
						bgColor,
						fontSize: selectFontSize,
						textColor,
						minHeight,
						maxHeight,
						dropdownIndicatorPadding,
					})}
					placeholder={placeholder}
					isClearable={isClearable}
					isDisabled={disabled}
					menuPortalTarget={menuPortalTarget}
					autoFocus={autoFocus}
					defaultMenuIsOpen={defaultMenuIsOpen}
					onBlur={onBlur}
				/>
			</div>
		</div>
	);
};

export default SelectInput;
